import { useState } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    const formData = new URLSearchParams();
    formData.append("email", email);
    try {
      await fetch("https://readdy.ai/api/form/d7bv5d8r4h7u93gnpvqg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setSubscribed(true);
    } catch {
      setSubscribed(true);
    } finally {
      setSubscribing(false);
    }
  };

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-stone-800 text-white">
      {/* Top Section */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/2de05ae8-ab7f-40ca-afb5-96f93dc606a2.png"
                alt="Rappy me Logo"
                className="w-12 h-12 object-contain"
              />
              <span
                className="font-bold text-lg text-white"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                娛樂紅歡密
              </span>
            </div>
            <p className="text-stone-400 text-sm leading-relaxed mb-3">
              讓每一次相遇，都成為生命中的驚喜。
              <br />
              台灣最溫暖的生活策劃平台。
            </p>
            <a
              href="mailto:red.shrimp.close@gmail.com"
              className="inline-flex items-center gap-2 text-stone-400 text-sm hover:text-red-400 transition-colors mb-5"
            >
              <i className="ri-mail-line" />
              red.shrimp.close@gmail.com
            </a>

            {/* Social Links */}
            <p className="text-stone-500 text-xs mb-3 uppercase tracking-widest">追蹤我們</p>
            <div className="flex flex-col gap-2.5">
              {[
                { icon: "ri-instagram-line", label: "@rappy.meme", href: "https://www.instagram.com/rappy.meme/", color: "hover:bg-pink-600" },
                { icon: "ri-facebook-fill", label: "娛樂紅歡密", href: "https://www.facebook.com/Red.shrimp.close/", color: "hover:bg-blue-600" },
                { icon: "ri-line-fill", label: "官方 LINE", href: "https://lin.ee/wpeGAKJ", color: "hover:bg-green-600" },
                { icon: "ri-mail-line", label: "寄信給我們", href: "mailto:red.shrimp.close@gmail.com", color: "hover:bg-red-600" },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target={social.href.startsWith("http") ? "_blank" : undefined}
                  rel="nofollow noreferrer"
                  className={`inline-flex items-center gap-2.5 px-3 py-2 rounded-lg bg-stone-700/60 text-stone-300 text-sm ${social.color} hover:text-white transition-all duration-200 cursor-pointer group`}
                >
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className={`${social.icon} text-base`} />
                  </span>
                  <span className="group-hover:translate-x-0.5 transition-transform duration-200">{social.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Activity Links */}
          <div>
            <h4 className="font-bold text-white text-base mb-4">
              <a href="#activity-categories" className="hover:text-red-400 transition-colors">活動分類</a>
            </h4>
            <ul className="flex flex-col gap-2.5">
              {["戶外冒險", "知性學習", "社交聯誼", "親子時光", "企業包場"].map((item) => (
                <li key={item}>
                  <a
                    href="#"
                    className="text-stone-400 text-sm hover:text-red-400 transition-colors"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* About Links */}
          <div>
            <h4 className="font-bold text-white text-base mb-4">
              <a href="#about-us" className="hover:text-red-400 transition-colors">關於我們</a>
            </h4>
            <ul className="flex flex-col gap-2.5">
              {["品牌故事", "團隊介紹", "合作夥伴", "媒體報導", "聯絡我們"].map((item) => (
                <li key={item}>
                  <a
                    href="#"
                    className="text-stone-400 text-sm hover:text-red-400 transition-colors"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* LINE QR */}
          <div>
            <h4 className="font-bold text-white text-base mb-2">加入官方 LINE</h4>
            <p className="text-stone-400 text-sm mb-4 leading-relaxed">
              掃描 QR Code 或點擊加入，<br />第一時間收到活動通知！
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://lin.ee/wpeGAKJ"
                target="_blank"
                rel="nofollow noreferrer"
                className="shrink-0 block"
              >
                <img
                  src="https://storage.readdy-site.link/project_files/57df84f3-5712-417f-a29d-2dea8cb5a0c9/2af448e5-6a1f-44c1-80ff-72cf57b8acee_unnamed.jpg?v=b4f1546ee772db1ed066ec9f52d31325"
                  alt="娛樂紅歡密 LINE QR Code"
                  className="w-24 h-24 rounded-xl object-cover"
                />
              </a>
              <div>
                <a
                  href="https://lin.ee/wpeGAKJ"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-line-fill text-base" />
                  點我加入 LINE
                </a>
                <p className="text-stone-500 text-xs mt-2">@娛樂紅歡密</p>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-bold text-white text-base mb-2">訂閱電子報</h4>
            <p className="text-stone-400 text-sm mb-4 leading-relaxed">
              第一時間收到活動通知與限定優惠
            </p>
            {subscribed ? (
              <div className="bg-red-900/30 border border-red-700/50 rounded-xl p-4 text-center">
                <i className="ri-check-line text-red-400 text-xl mb-1 block" />
                <p className="text-red-300 text-sm">訂閱成功！感謝你的支持</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="newsletter-form"
                onSubmit={handleSubscribe}
                className="flex flex-col gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="輸入你的 Email"
                  required
                  className="w-full px-4 py-2.5 rounded-xl bg-stone-700 border border-stone-600 text-white text-sm placeholder-stone-400 focus:outline-none focus:border-red-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="w-full py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {subscribing ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      立即訂閱
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Quote */}
      <div className="border-t border-stone-700">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p
              className="text-stone-400 text-sm text-center md:text-left"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              「在紅歡密，露營不只是換個地方睡覺，而是換一種方式對話。」
            </p>
            <p className="text-stone-500 text-xs whitespace-nowrap">
              © 2026 娛樂紅歡密 Red Joy Mystery. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
